import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { clearSessionId } from "../utils/sessionId";

const CONSENT_STORAGE_KEY = "wayli.cookieConsent.v1";
const CONSENT_VERSION = 2;
const DEFAULT_CONSENT = {
  version: CONSENT_VERSION,
  necessary: true,
  functional: false,
  analytics: false,
  updatedAt: null,
};

const hasOwn = (value, key) => Object.prototype.hasOwnProperty.call(value ?? {}, key);

const buildConsentRecord = (settings = {}) => {
  const functional = hasOwn(settings, "functional") ? Boolean(settings.functional) : false;
  const analytics = hasOwn(settings, "analytics") ? Boolean(settings.analytics) : false;

  return {
    ...DEFAULT_CONSENT,
    functional,
    analytics,
    updatedAt: new Date().toISOString(),
  };
};

const readStoredConsent = () => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") {
      return null;
    }

    return buildConsentRecord(parsed);
  } catch {
    return null;
  }
};

const persistConsent = (consent) => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(consent));
  } catch {
    // Ignore storage failures; consent will fall back to the current session only.
  }
};

const ConsentContext = createContext({
  consent: null,
  effectiveConsent: DEFAULT_CONSENT,
  hasConsented: false,
  canUseFunctional: false,
  canUseAnalytics: false,
  isPreferencesOpen: false,
  acceptNecessaryAndFunctional: () => undefined,
  acceptAll: () => undefined,
  rejectOptional: () => undefined,
  savePreferences: () => undefined,
  openPreferences: () => undefined,
  closePreferences: () => undefined,
});

export const ConsentProvider = ({ children }) => {
  const [consent, setConsent] = useState(readStoredConsent);
  const [isPreferencesOpen, setIsPreferencesOpen] = useState(false);

  const applyConsent = (settings) => {
    const next = buildConsentRecord(settings);
    setConsent(next);
    persistConsent(next);
    setIsPreferencesOpen(false);
  };

  useEffect(() => {
    if (consent && !consent.analytics) {
      clearSessionId();
    }
  }, [consent]);

  const value = useMemo(
    () => ({
      consent,
      effectiveConsent: consent ?? DEFAULT_CONSENT,
      hasConsented: Boolean(consent),
      canUseFunctional: Boolean(consent?.functional),
      canUseAnalytics: Boolean(consent?.analytics),
      isPreferencesOpen,
      acceptNecessaryAndFunctional: () => applyConsent({ functional: true, analytics: false }),
      acceptAll: () => applyConsent({ functional: true, analytics: true }),
      rejectOptional: () => applyConsent({ functional: false, analytics: false }),
      savePreferences: (settings) => applyConsent(settings),
      openPreferences: () => setIsPreferencesOpen(true),
      closePreferences: () => {
        if (consent) {
          setIsPreferencesOpen(false);
        }
      },
    }),
    [consent, isPreferencesOpen]
  );

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
};

export const useConsent = () => useContext(ConsentContext);
